import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiSmartphone } from 'react-icons/fi';
import SafeIcon from './SafeIcon';
import { supabase } from '@/integrations/supabase/client';

const TwoFactorVerify = ({ email, method = 'totp', phoneLast4, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const isPhoneMethod = method === 'phone';

  // Send SMS code on mount for phone method
  useEffect(() => {
    if (isPhoneMethod && !codeSent) {
      sendSmsCode();
    }
  }, [isPhoneMethod]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendSmsCode = async () => {
    try {
      setSendingCode(true);
      setError('');

      const { data, error: sendError } = await supabase.functions.invoke('send-sms-code', {
        body: { email, action: 'login' }
      });

      if (sendError) throw sendError;

      if (!data.success) {
        setError(data.error || 'Failed to send SMS code');
        return;
      }

      setCodeSent(true);
      setCountdown(60); // 60 seconds cooldown for resend
    } catch (err) {
      console.error('Send SMS error:', err);
      setError('Failed to send SMS code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (isPhoneMethod) {
        // Verify SMS code
        const { data, error: verifyError } = await supabase.functions.invoke('verify-sms-code', {
          body: { email, code }
        });

        if (verifyError) throw verifyError;

        if (!data.success || !data.valid) {
          setError(data.error || 'Invalid verification code. Please try again.');
          return;
        }
      } else {
        // Verify TOTP code
        const { data, error: verifyError } = await supabase.functions.invoke('verify-totp', {
          body: { action: 'verify-login', email, code }
        });

        if (verifyError) throw verifyError;

        if (!data.success || !data.valid) {
          setError('Invalid verification code. Please try again.');
          return;
        }
      }

      onSuccess();
    } catch (err) {
      console.error('2FA verification error:', err);
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
          <SafeIcon 
            icon={isPhoneMethod ? FiSmartphone : FiShield} 
            className="w-8 h-8 text-cyan-400" 
          />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Two-Factor Authentication</h3>
        <p className="text-gray-400 text-sm">
          {isPhoneMethod ? (
            <>
              Enter the 6-digit code sent to your phone
              {phoneLast4 && <span className="text-cyan-400"> (***{phoneLast4})</span>}
            </>
          ) : (
            'Enter the 6-digit code from your Google Authenticator app'
          )}
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
          {error}
        </div>
      )}

      {isPhoneMethod && sendingCode && (
        <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm text-center">
          Sending verification code...
        </div>
      )}

      <div>
        <input
          type="text"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          autoFocus
          className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-4 text-white text-center text-2xl font-mono tracking-widest placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent"
        />
      </div>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-3 px-4 rounded-xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 text-gray-300 hover:text-white transition-all font-medium"
        >
          Cancel
        </button>
        <button
          onClick={handleVerify}
          disabled={loading || code.length !== 6}
          className="flex-1 py-3 px-4 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium disabled:opacity-50 transition-all flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            'Verify'
          )}
        </button>
      </div>

      {isPhoneMethod ? (
        <div className="text-center">
          {countdown > 0 ? (
            <p className="text-gray-500 text-xs">
              Resend code in {countdown}s
            </p>
          ) : (
            <button
              onClick={sendSmsCode}
              disabled={sendingCode}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
            >
              Resend code
            </button>
          )}
        </div>
      ) : (
        <p className="text-center text-gray-500 text-xs">
          Open Google Authenticator on your phone to get the code
        </p>
      )}
    </motion.div>
  );
};

export default TwoFactorVerify;
